
import { mapGetters } from 'vuex'
import yearbook from '../../api/yearbook'
import { getColorForEnergyName, getColorForRegionCode } from '../../utils/colorFunctions'
import chartType from '../../Enum/chartType'
import {
  getEnergyCode,
  getNumberFormatStringForNbDecimal,
  getShortTitleForEnergyCode
} from '../../utils/yearbookFunctions'
import EnerLoader from '../ui/enerLoader'
import { capitalize } from '../../utils/StringUtils'
import { clone, getPivotArray } from '@/utils/enerFunctions'
import EnerEchartsBar from '@/components/enerEcharts/enerEchartsBar'
import EnerEchartsPie from '@/components/enerEcharts/enerEchartsPie'

export default {
  name: 'ChartContainer',
  components: { EnerLoader, EnerEchartsPie, EnerEchartsBar },
  props: {
    config: {
      type: Object,
      required: true
    },
    showLegend: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data () {
    return {
      apiData: [],
      isChartReady: false,
      initOptions: {
        height: null
      },
      updateOptions: {
        notMerge: true
      }
    }
  },
  async fetch () {
    const serieFullCodes = this.config.items.map(item => item + '_' + this.config.displayUnit)
    const years = this.computedYears
    let zoneCodes
    if (this.benchmarkCountries.length > 0) {
      zoneCodes = this.benchmarkCountries.map(country => country.nrdCode)
    } else if (this.mapCountry !== this.world) {
      zoneCodes = [this.mapCountry.nrdCode]
    } else {
      switch (this.config.type) {
        case chartType.COLUMN_STACKED:
          if (this.config.items.length === 1 && this.config.items[0] === 'ietotcpppa') {
            zoneCodes = ['monde']
          } else {
            zoneCodes = this.regions.map(region => region.nrdCode)
          }
          break
        case chartType.DONUT:
          zoneCodes = ['monde']
          break
        case chartType.COLUMN:
          zoneCodes = this.regions.map(region => region.nrdCode)
          break
        case chartType.BAR:
          zoneCodes = ['monde']
          break
      }
    }
    const requestParams = {
      fullCode: serieFullCodes,
      zoneCode: zoneCodes,
      date: years,
      pagination: false
    }
    this.apiData = await yearbook.values(requestParams, this)
  },
  computed: {
    ...mapGetters({
      regions: 'regions',
      world: 'world',
      defaultChartZoneCodes: 'defaultChartZoneCodes',
      mapCountry: 'page/mapCountry',
      benchmarkCountries: 'page/benchmarkCountries'
    }),
    computedYears () {
      if ((this.benchmarkCountries.length > 0 || this.mapCountry !== this.world) && this.config.years.length === 1 && this.config.type === chartType.COLUMN) {
        return []
      }
      return this.config.years
    },
    chartDataSetOrderedBy () {
      if (this.benchmarkCountries.length > 0) {
        return this.benchmarkCountries.map(benchmarkCountry => benchmarkCountry.nrdCode)
      }
      if (this.baseChartType === 'pie') {
        return null
      }
      if (this.config.items.length === 1) {
        if (
          this.config.items[0] === 'ietotcpppa' ||
          this.mapCountry !== this.world ||
          this.computedYears.length === 1
        ) {
          return null
        } else {
          return this.defaultChartZoneCodes
        }
      }
      return this.config.items
    },
    unitName () {
      return this.$options.filters.unitName(this.config.displayUnit)
    },
    numberFormat () {
      return getNumberFormatStringForNbDecimal(this.config.nbDecimale)
    },
    baseChartType () {
      switch (this.config.type) {
        case chartType.DONUT:
          return 'pie'
        case chartType.LINE:
          return 'line'
        default :
          return 'bar'
      }
    },
    isStacked () {
      return this.config.type === chartType.COLUMN_STACKED && this.benchmarkCountries.length === 0
    },
    isHorizontal () {
      return this.config.type === chartType.BAR
    },
    isScrollableX () {
      return this.computedYears.length !== 1
    },
    isValueSorted () {
      return this.config.type === chartType.COLUMN && this.computedYears.length === 1
    },
    highlightShortTitle () {
      return getShortTitleForEnergyCode(getEnergyCode(this.config.highlight), this.$i18n)
    },
    categoryField () {
      if (this.computedYears.length === 1) {
        if (this.config.items.length === 1) {
          return 'zoneName'
        } else {
          return 'year'
        }
      } else {
        return 'year'
      }
    },
    chartDataset () {
      let chartDataset = []
      let regionCodes = []
      const categoryField = this.categoryField
      let nameField = 'zoneCodeNrd'
      let valueField = 'value'
      if (this.apiData.length > 0) {
        if (this.computedYears.length === 1) {
          if (this.config.items.length === 1) {
            nameField = 'year'
            valueField = 'value'
          } else if (this.baseChartType === 'pie') {
            nameField = 'serieShortTitle'
            valueField = 'value'
          } else {
            nameField = 'serieCode'
            valueField = 'value'
          }
        }
        if (this.benchmarkCountries.length > 0) {
          regionCodes = this.benchmarkCountries.map(item => item.nrdCode)
        } else if (this.config.countries.length > 0) {
          regionCodes = this.config.countries
        } else if (this.mapCountry !== this.world) {
          regionCodes = [this.mapCountry.nrdCode]
        } else {
          regionCodes = this.defaultChartZoneCodes
        }
        const sortedApiData = clone(this.apiData)
        if (this.isValueSorted === true) {
          sortedApiData.sort((a, b) => {
            return a.value - b.value
          })
        } else {
          sortedApiData.sort((a, b) => {
            return a.year - b.year
          })
        }
        chartDataset = getPivotArray(sortedApiData, categoryField, nameField, valueField)
        // ensure serie/value order
        chartDataset.sort((a, b) => {
          if (a[0] === categoryField) {
            return -1
          } else if (b[0] === categoryField) {
            return 1
          } else {
            return regionCodes.indexOf(a[0]) - regionCodes.indexOf(b[0])
          }
        })
        if (this.baseChartType === 'pie') {
          for (let i = 0; i < chartDataset.length; i++) {
            if (chartDataset[i][0] === categoryField) {
              chartDataset[i].push('color')
              chartDataset[i].push('highlighted')
            } else {
              const valueObject = sortedApiData.find(value => value.serieShortTitle === chartDataset[i][0])
              chartDataset[i].push(getColorForEnergyName(valueObject.energyCode))
              chartDataset[i].push(true)
            }
          }
        } else if (this.chartDataSetOrderedBy !== null) {
          const chartDataSetOrdered = []
          chartDataSetOrdered.push(chartDataset[0])
          const n = this.chartDataSetOrderedBy.length
          const m = chartDataset.length
          for (let i = 0; i < n; i++) {
            const serieBy = this.chartDataSetOrderedBy[i]
            for (let j = 1; j < m; j++) {
              const chartDatasetItem = chartDataset[j]
              if (chartDatasetItem[0] === serieBy) {
                chartDataSetOrdered.push(chartDatasetItem)
                break
              }
            }
          }
          chartDataset = chartDataSetOrdered
        }
      }
      return chartDataset
    },
    chartSeries () {
      const chartSeries = []
      const scope = this
      if (this.baseChartType === 'pie') {
        const chartSerie = {
          label: {
            show: false,
            formatter (param) {
              return param.percent.toFixed(0) + '%'
            }
          },
          itemStyle: {
            color (param) {
              return param.data[2]
            }
          },
          labelLine: {
            show: false
          },
          type: 'pie',
          radius: ['80%', '90%'],
          top: 0,
          bottom: 40
        }
        chartSeries.push(chartSerie)
      } else if (this.computedYears.length === 1) {
        if (this.config.items.length === 1) {
          let serieData = [...this.chartDataset[1]]
          serieData.shift()
          serieData = serieData.map(data => Math.abs(data))
          const serieDataMax = Math.max(...serieData)
          const chartSerie = {
            name: this.computedYears[0],
            type: this.baseChartType,
            seriesLayoutBy: 'row',
            label: {
              show: true,
              position: 'top',
              formatter: (params) => {
                if (params.data[1] === undefined || params.data[1] === null) {
                  return ''
                }
                if (Math.abs(params.data[1]) < (serieDataMax / 60)) {
                  return params.data[1].toFixed(scope.config.nbDecimale)
                } else {
                  return ''
                }
              }
            }
          }
          chartSeries.push(chartSerie)
        } else {
          const n = this.config.items.length
          for (let i = 0; i < n; i++) {
            const item = this.config.items[i]
            const serieData = this.chartDataset.find(dataLine => item === dataLine[0])
            if (serieData !== undefined) {
              const energyCode = getEnergyCode(item)
              const chartSerie = {
                name: getShortTitleForEnergyCode(energyCode, this.$i18n),
                type: this.baseChartType,
                color: getColorForEnergyName(energyCode),
                seriesLayoutBy: 'row'
              }
              chartSeries.push(chartSerie)
            }
          }
        }
      } else if (this.benchmarkCountries.length > 0) {
        const n = this.benchmarkCountries.length
        for (let i = 0; i < n; i++) {
          const country = this.benchmarkCountries[i]
          const serieData = this.chartDataset.find(dataLine => country.nrdCode === dataLine[0])
          if (serieData !== undefined) {
            const chartSerie = {
              name: country['name' + capitalize(this.$i18n.locale)],
              type: this.baseChartType,
              seriesLayoutBy: 'row'
            }
            chartSeries.push(chartSerie)
          }
        }
      } else if (this.config.items.length === 1 && this.config.items[0] === 'ietotcpppa') {
        const chartSerie = {
          name: this.mapCountry['name' + capitalize(this.$i18n.locale)],
          type: this.baseChartType,
          seriesLayoutBy: 'row'
        }
        chartSeries.push(chartSerie)
      } else if (this.mapCountry !== this.world) {
        const chartSerie = {
          name: this.mapCountry['name' + capitalize(this.$i18n.locale)],
          type: this.baseChartType,
          seriesLayoutBy: 'row'
        }
        chartSeries.push(chartSerie)
      } else {
        const n = this.defaultChartZoneCodes.length
        for (let i = 0; i < n; i++) {
          const chartZoneCode = this.defaultChartZoneCodes[i]
          const region = this.regions.find(region => region.nrdCode === chartZoneCode)
          const serieData = this.chartDataset.find(dataLine => chartZoneCode === dataLine[0])
          if (serieData !== undefined) {
            const chartSerie = {
              name: region['name' + capitalize(this.$i18n.locale)],
              type: this.baseChartType,
              color: getColorForRegionCode(region.nrdCode),
              seriesLayoutBy: 'row'
            }
            if (this.isStacked) {
              chartSerie.stack = 'total'
            }
            chartSeries.push(chartSerie)
          }
        }
      }
      return chartSeries
    },
    option () {
      const scope = this
      const option = {
        tooltip: {
          trigger: 'item',
          showDelay: 0,
          transitionDuration: 0.2,
          formatter (params) {
            if (params.data === undefined) {
              return null
            }
            if (scope.baseChartType === 'pie') {
              return params.data[0] + ': <b>' + params.percent.toFixed(scope.config.nbDecimale) + '</b> %'
            } else {
              return params.seriesName + ' ' + params.data[0] + ': <b>' + params.data[params.seriesIndex + 1].toFixed(scope.config.nbDecimale) + '</b> ' + scope.unitName
            }
          }
        },
        dataset: {
          source: this.chartDataset
        },
        series: this.chartSeries
      }
      if (this.baseChartType !== 'pie') {
        if (this.isHorizontal) {
          option.yAxis = {
            type: 'category'
          }
          option.xAxis = {
            type: 'value'
            // interval: 50,
            // min (value) {
            //   return (Math.floor((Math.floor(value.min / 10) * 10) / 50)) * 50
            // },
            // max (value) {
            //   return (Math.ceil((Math.ceil(value.max / 10) * 10) / 50)) * 50
            // }
          }
        } else {
          option.grid = {
            right: '5%'
          }
          option.xAxis = {
            type: 'category'
          }
          if (this.computedYears.length === 1 && this.config.items.length === 1) {
            option.xAxis.axisLabel = {
              interval: 0,
              rotate: 45
            }
          }
          option.yAxis = {
            name: this.unitName,
            nameLocation: 'center',
            nameGap: 50,
            nameRotate: 90,
            nameTextStyle: {
              fontWeight: 'bold',
              fontSize: 14
            }
          }
        }
      }
      if (this.showLegend && (option.series.length > 1 || this.benchmarkCountries.length > 0 || this.baseChartType === 'pie')) {
        option.legend = {
          top: 'bottom'
        }
      }
      if (this.isScrollableX) {
        option.dataZoom = [
          {
            type: 'slider',
            top: 0,
            showDataShadow: false,
            brushSelect: false,
            handleSize: 20,
            height: 20
          }
        ]
      }
      return option
    }

  },
  watch: {
    mapCountry () {
      this.$fetch()
    },
    benchmarkCountries () {
      this.$fetch()
    }
  },
  fetchOnServer: false
}
